import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Button,
  DateRangeSelector,
  ENUM_DATE_RANGE,
  ToastMessage,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { BackIcon } from 'cokitchen-icons';
import { ExtendedFilterType } from './types';
import { IssueType } from 'models/issue.model';
import { apiService } from 'utils/api';
import ExportButton from 'components/base/export-button';
import CreateIssue from 'components/cs/pending-tasks/create-issue';
import InfiniteScroll from 'react-infinite-scroll-component';
import MarkAsDoneModal from 'components/cs/pending-tasks/mark-as-done-modal';
import RepIssueCard from 'components/cs/reps-details/rep-issue-card';
import ExportModal from 'components/cs/modals/export-modal';
import ReviewModal from 'components/cs/pending-tasks/review-modal/review-modal';
import useRoleContext from 'contexts/role.context';
import CSTab, { CSTabListType, CSTabType } from '../../../components/base/cs-tab';
import { toast } from 'react-toastify';
import Slider, { Settings } from 'react-slick';
import EscalateIssueModal from './escalate-issue-modal';
import { STORAGE_KEYS } from 'utils/constants';
import { ENUM_ROLE } from '@cokitchen/cokitchen-auth';

const ALL_TABS: Record<ExtendedFilterType, string> = {
  ALL: 'All',
  KITCHEN: 'Kitchen',
  REVIEW: 'Review',
  LOGISTICS: 'Logistics',
  TECH_TEAM: 'Tech Team',
  FC_SHOP: 'FC Shop',
  CUSTOMER_SERVICE: 'Customer Service',
  BANK_AND_FINANCE: 'Bank and Finance',
  OTHER: 'Other'
};
const navStyles = {
  active: 'border-b border-b-[4px] border-b-[#E60A2B] mb-0 text-[#1A1A1A]',
  inactive: 'text-[#858585]'
};
export type selectedRep = {
  name: string;
  id: string;
};

const PendingTasks = () => {
  const isMounted = useRef(false);
  const [activeTab, setActiveTab] = useState<ExtendedFilterType>('ALL');
  const [loading, setLoading] = useState(true);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState({} as IssueType);
  const [showCompleted, setShowCompleted] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [issues, setIssues] = useState<IssueType[]>([]);
  const [assignedIssues, setAssignedIssues] = useState<IssueType[]>([]);
  const [escalatedIssues, setEscalatedIssues] = useState<IssueType[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalNumber, setTotalNumber] = useState(0);
  const [filters, setFilters] = useState(getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK));
  const [activeRepTab, setActiveRepTab] = useState<CSTabType>('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [selectedCsRep, setSelectedCsRep] = useState({} as selectedRep);
  const [resolution, setResolution] = useState<string>('');
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  const fetchCurrentTask = async (isNew?: boolean) => {
    if (isNew) setLoading(true);
    const { startDate: start_date, endDate: end_date } = filters;
    const params: any = {
      page: isNew ? 1 : pageNumber,
      start_date,
      end_date,
      cokitchen_id: cokitchenId
    };
    if (activeTab !== 'ALL') params.feedback_destination = activeTab;
    const state = showCompleted ? 'resolved' : 'pending';
    try {
      const res = await apiService.get(`cs/${state}/issues`, {
        params,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const newIssues = res.data.data ?? [];
      const allIssues = isNew ? newIssues : [...issues, ...newIssues];
      setIssues(allIssues);
      setTotalNumber(res.data?.pagination?.total);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };

  const fetchAssignedIssues = async (isNew?: boolean) => {
    if (isNew) setLoading(true);
    const { startDate: start_date, endDate: end_date } = filters;
    const params: any = {
      start_date,
      end_date,
      page: isNew ? 1 : pageNumber,
      cokitchen_id: cokitchenId
    };
    if (activeTab !== 'ALL') params.channel = activeTab;
    const state = showCompleted ? 'resolved' : 'pending';
    try {
      const res = await apiService.get(`cs/assigned/${state}/issue`, {
        params,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const newIssues = res.data.results ?? [];
      const allIssues = isNew ? newIssues : [...issues, ...newIssues];
      setAssignedIssues(allIssues);
      setTotalNumber(res.data?.total);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };

  const fetchEscalatedIssues = async (isNew?: boolean) => {
    const { startDate: start_date, endDate: end_date } = filters;
    const params: any = {
      start_date,
      end_date,
      page: isNew ? 1 : pageNumber,
      cokitchen_id: cokitchenId
    };
    if (activeTab !== 'ALL') params.department = activeTab;
    try {
      const res = await apiService.get(`cs/all/lead/issue`, {
        params,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const newIssues = res.data.results ?? [];
      const escalatedIssues = isNew ? newIssues : [...issues, ...newIssues];
      setEscalatedIssues(escalatedIssues);
      setTotalNumber(res.data?.total);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };

  const fetchActiveTab = () => {
    if (activeRepTab === 'all') {
      fetchCurrentTask(true);
    } else if (activeRepTab === 'assigned') {
      fetchAssignedIssues(true);
    } else if (activeRepTab === 'escalated') {
      fetchEscalatedIssues(true);
    }
  };
  useEffect(() => {
    fetchActiveTab();
  }, [activeRepTab]);

  useEffect(() => {
    if (pageNumber > 1) fetchCurrentTask();
  }, [pageNumber]);

  useEffect(() => {
    if (isMounted.current) {
      setTotalNumber(0);
      setHasMore(false);
      setPageNumber(1);
      fetchActiveTab();
    }
  }, [showCompleted, activeTab, filters, activeRepTab]);

  useEffect(() => {
    setHasMore(issues.length < totalNumber);
  }, [issues]);

  const fetchMoreData = () => {
    setPageNumber((p) => p + 1);
  };

  const openMarkModal = (issue: IssueType) => {
    setSelectedIssue(issue);
  };

  const openReviewModal = (issue: IssueType) => {
    setShowReviewModal(true);
    setSelectedIssue(issue);
  };
  const addPendingIssue = (issue: IssueType) => {
    if (!showCompleted) {
      setIssues([issue, ...issues]);
      setTotalNumber(totalNumber + 1);
      setRefreshKey(refreshKey + 1);
    }
  };

  const removeDoneIssue = () => {
    const newIssues = issues.filter((p) => p.id !== selectedIssue.id);
    setIssues(newIssues);
    setTotalNumber(totalNumber - 1);
    setRefreshKey(refreshKey + 1);
  };

  const { role } = useRoleContext();

  const isCSRep = () =>
    [ENUM_ROLE.CS_OUTBOUND, ENUM_ROLE.CS, ENUM_ROLE.CS_INBOUND].includes(role as ENUM_ROLE);
  const isCSSuperAdmin = () => role === ENUM_ROLE.CS_SUPER_ADMIN;

  const getAllIssues = () => fetchCurrentTask(true);
  const getAssignedIssues = () => fetchAssignedIssues(true);
  const getEscalatedIssues = () => fetchEscalatedIssues(true);

  const TAB_LIST: CSTabListType[] = [
    {
      text: 'All Issues',
      value: 'all',
      className: 'rounded-l-md',
      onClick: getAllIssues
    },
    {
      text: `Assigned to Me (${assignedIssues.length})`,
      value: 'assigned',
      className: `rounded-r-md ${isCSRep() ? 'block' : 'hidden'}`,
      onClick: getAssignedIssues
    },
    {
      text: `Escalated Issues (${escalatedIssues.length})`,
      value: 'escalated',
      className: `rounded-r-md ${isCSSuperAdmin() ? 'block' : 'hidden'}`,
      onClick: getEscalatedIssues
    }
  ];
  const filteredIssues =
    activeRepTab === 'all'
      ? issues
      : activeRepTab === 'assigned'
      ? assignedIssues
      : activeRepTab === 'escalated'
      ? escalatedIssues
      : [];

  const getStateText = () => {
    let text;
    if (showCompleted) {
      text = 'completed';
    } else {
      if (activeRepTab === 'escalated') {
        text = 'escalated';
      } else if (activeRepTab === 'assigned') {
        text = 'assigned';
      } else {
        text = 'pending';
      }
    }
    return text;
  };
  const sliderSettings: Settings = {
    dots: false,
    infinite: false,
    speed: 500,
    autoplay: false,
    variableWidth: true,
    slidesToShow: 6,
    slidesToScroll: 2,
    centerMode: false,
    swipe: true,
    draggable: true,
    prevArrow: (
      <svg
        width='12'
        height='12'
        viewBox='0 0 12 12'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M6 0.625C8.965 0.625 11.375 3.035 11.375 6C11.375 8.965 8.965 11.375 6 11.375C3.035 11.375 0.625 8.965 0.625 6C0.625 3.035 3.035 0.625 6 0.625ZM6 10.625C8.55 10.625 10.625 8.55 10.625 6C10.625 3.45 8.55 1.375 6 1.375C3.45 1.375 1.375 3.45 1.375 6C1.375 8.55 3.45 10.625 6 10.625Z'
          fill='#858585'
        />
        <path
          d='M6.63 3.86C6.725 3.86 6.82 3.895 6.895 3.97C7.04 4.115 7.04 4.355 6.895 4.5L5.395 6L6.895 7.5C7.04 7.645 7.04 7.885 6.895 8.03C6.75 8.175 6.51 8.175 6.365 8.03L4.6 6.265C4.455 6.12 4.455 5.88 4.6 5.735L6.365 3.97C6.44 3.895 6.535 3.86 6.63 3.86Z'
          fill='#858585'
        />
      </svg>
    ),
    nextArrow: (
      <svg
        width='12'
        height='13'
        viewBox='0 0 12 13'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M6 11.875C3.035 11.875 0.625 9.465 0.625 6.5C0.625 3.535 3.035 1.125 6 1.125C8.965 1.125 11.375 3.535 11.375 6.5C11.375 9.465 8.965 11.875 6 11.875ZM6 1.875C3.45 1.875 1.375 3.95 1.375 6.5C1.375 9.05 3.45 11.125 6 11.125C8.55 11.125 10.625 9.05 10.625 6.5C10.625 3.95 8.55 1.875 6 1.875Z'
          fill='#858585'
        />
        <path
          d='M5.37 8.63996C5.275 8.63996 5.18 8.60496 5.105 8.52996C4.96 8.38496 4.96 8.14496 5.105 7.99996L6.605 6.49996L5.105 4.99996C4.96 4.85496 4.96 4.61496 5.105 4.46996C5.25 4.32496 5.49 4.32496 5.635 4.46996L7.4 6.23496C7.545 6.37996 7.545 6.61996 7.4 6.76496L5.635 8.52996C5.56 8.60496 5.465 8.63996 5.37 8.63996Z'
          fill='#858585'
        />
      </svg>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 2
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 2
        }
      }
    ]
  };

  const exportIssues = async (
    email: string,
    dateRange: {
      startDate: Date;
      endDate: Date;
    }
  ) => {
    const { startDate: start_date, endDate: end_date } = dateRange;
    try {
      await apiService.get(`cs/issue/export/csv/${showCompleted ? 'resolved' : 'pending'}`, {
        params: {
          email,
          start_date,
          end_date
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Data exported successfully' />);
      setShowExportModal(false);
    } catch (err) {
      toast(<ToastMessage type='error' message='Failed to export data' />);
    }
  };

  const toggleEscalateModal = () => {
    setShowEscalateModal(!showEscalateModal);
  };
  return (
    <Fragment>
      <div className='flex flex-wrap gap-4 justify-between mb-6'>
        <div>
          <h1 className='font-semibold text-2xl mb-2'>
            {showCompleted ? 'Completed' : 'Pending'} Tasks ({totalNumber})
          </h1>
          {(isCSRep() || isCSSuperAdmin()) && (
            <CSTab list={TAB_LIST} active={activeRepTab} setActive={setActiveRepTab} />
          )}
          {showCompleted ? (
            <Button variant='link' color='gray' onClick={() => setShowCompleted(false)}>
              <BackIcon className='w-4 h-4' /> Back to Pending tasks
            </Button>
          ) : (
            <Button
              variant='link'
              color='primary'
              className={`underline`}
              onClick={() => setShowCompleted(true)}>
              Go to Completed Tasks
            </Button>
          )}
        </div>
        <div className='flex gap-3 items-center'>
          <DateRangeSelector showTime onChange={(v) => setFilters(v.date)} />
        </div>
      </div>
      <div className='flex justify-between items-center gap-4 border-b border-gray-border flex-wrap'>
        <div className='w-full max-w-[800px]'>
          <Slider {...sliderSettings}>
            {Object.entries(ALL_TABS).map(([key, value], index) => (
              <li
                role='button'
                key={index}
                onClick={() => setActiveTab(key as ExtendedFilterType)}
                className={`flex mr-6 last:mr-0 cursor-pointer py-4 whitespace-nowrap
            ${key === activeTab ? navStyles.active : navStyles.inactive}`}>
                {value}
              </li>
            ))}
          </Slider>
        </div>
        <div className='flex gap-6'>
          <ExportButton onClick={() => setShowExportModal(true)} />
          <CreateIssue onCreateNewIssue={addPendingIssue} />
        </div>
      </div>

      <div key={refreshKey}>
        {loading ? (
          <div className='text-2xl leading-[40vh] text-center'>Loading....</div>
        ) : issues.length === 0 ? (
          <div className='text-2xl leading-[40vh] text-center'>
            There are no {getStateText()} issues
            {activeTab !== 'ALL' ? ' with the selected filter' : ''}
          </div>
        ) : (
          <InfiniteScroll
            hasMore={hasMore}
            next={() => fetchMoreData()}
            dataLength={issues.length}
            loader={<p className='text-center mt-8'>Loading more issues...</p>}
            endMessage={<p className='text-center mt-8'>End of list</p>}>
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6'>
              {filteredIssues?.map((t) => (
                <RepIssueCard
                  handleMenuClick={() => setSelectedIssue(t)}
                  issue={t}
                  key={t.id}
                  isComplete={showCompleted}
                  openReview={() => openReviewModal(t)}
                  setShowEscalateModal={setShowEscalateModal}
                  markIssue={() => openMarkModal(t)}
                />
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
      <MarkAsDoneModal
        show={showMarkModal}
        onClose={() => {
          setResolution('');
          setSelectedCsRep({} as selectedRep);
          setShowMarkModal(false);
        }}
        onSuccess={removeDoneIssue}
        issue={selectedIssue}
        selectedCsRep={selectedCsRep}
        resolution={resolution}
      />
      <ExportModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        title='Export Issues'
        onSubmit={(
          email: string,
          dateRange: {
            startDate: Date;
            endDate: Date;
          }
        ) => exportIssues(email, dateRange)}
      />
      <ReviewModal
        show={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
        }}
        type={showCompleted ? 'resolved' : 'unresolved'}
        issue={selectedIssue}
        resolution={resolution}
        setResolution={setResolution}
        selectedCsRep={selectedCsRep}
        setSelectedCsRep={setSelectedCsRep}
        setShowMarkModal={setShowMarkModal}
      />
      <EscalateIssueModal
        show={showEscalateModal}
        onClose={toggleEscalateModal}
        issue={selectedIssue}
      />
    </Fragment>
  );
};

export default PendingTasks;
